import React, { useState, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { API_ENDPOINT } from '../utils/api';
import { Upload, FileText, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormField, FormSelect, Widget } from './Common';

function UploadView({ onUploadComplete }) {
  const { organization, user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    incident_name: '',
    incident_date: '',
    hazard_type: '',
    incident_type: ''
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [existingNames, setExistingNames] = useState([]);

  // Load existing event names for duplicate detection
  React.useEffect(() => {
    const loadExistingNames = async () => {
      if (!organization?.organizationId) return;
      try {
        const { fetchWithAuth } = await import('../utils/api');
        const data = await fetchWithAuth('/dashboard/reports', organization.organizationId);
        if (data?.reports) {
          setExistingNames(data.reports.map(r => r.incident_name?.toLowerCase().trim()).filter(Boolean));
        }
      } catch (e) {
        console.error('Could not load existing event names:', e);
      }
    };
    loadExistingNames();
  }, [organization]);

  // Check for duplicate name when user types
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setFormData({ ...formData, incident_name: newName });
    
    if (newName.trim() && existingNames.includes(newName.toLowerCase().trim())) {
      setDuplicateWarning(`An event named "${newName}" already exists. You can still create this event if needed.`);
    } else {
      setDuplicateWarning(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!organization?.organizationId) {
      toast.error('Organization not loaded. Please refresh the page.');
      return;
    }

    // If duplicate warning is showing, confirm before proceeding
    if (duplicateWarning) {
      if (!window.confirm(`An event with this name already exists. Are you sure you want to create another event with the same name?`)) {
        return;
      }
    }
    
    setUploading(true);
    const uploadToast = toast.loading('Uploading files...');

    try {
      const filePromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              filename: file.name,
              file_type: file.name.split('.').pop().toLowerCase(),
              content: reader.result.split(',')[1]
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const encodedFiles = await Promise.all(filePromises);

      const response = await fetch(`${API_ENDPOINT}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...formData, 
          files: encodedFiles,
          organization_id: organization.organizationId,
          auto_generate: autoGenerate,
          created_by: organization?.userName || user?.email || 'Unknown'
        })
      });

      const result = await response.json();

      if (response.ok) {
        if (autoGenerate) {
          toast.success('Event created and AAR generation started!', {
            id: uploadToast,
            duration: 3000,
          });
        } else {
          toast.success('Event created successfully!', {
            id: uploadToast,
            duration: 3000,
          });
        }
        
        // Navigate to the new event details page after a brief delay
        setTimeout(() => {
          // Pass the aar_id to onUploadComplete so it can navigate
          onUploadComplete(result.aar_id);
          
          // Reset form
          setFormData({ incident_name: '', incident_date: '', hazard_type: '', incident_type: '' });
          setFiles([]);
          setAutoGenerate(false);
        }, 1500);
      } else {
        toast.error(`Error: ${result.error}`, { id: uploadToast });
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`, { id: uploadToast });
    }

    setUploading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Widget title="Create New Event" subtitle="Upload documents and provide incident details">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FormField
                label="Incident/Event Name"
                required
                value={formData.incident_name}
                onChange={handleNameChange}
                placeholder="e.g., Hurricane Response Exercise 2025"
              />
              {duplicateWarning && (
                <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-amber-800">{duplicateWarning}</p>
                </div>
              )}
            </div>
            <FormField
              label="Incident/Event Date"
              type="date"
              required
              value={formData.incident_date}
              onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
            />
            <FormSelect
              label="Incident Type"
              required
              value={formData.incident_type}
              onChange={(e) => setFormData({ ...formData, incident_type: e.target.value })}
              options={[
                { value: '', label: 'Select type...' },
                { value: 'incident', label: 'Incident' },
                { value: 'public_event', label: 'Public Event' },
                { value: 'training', label: 'Training' },
                { value: 'exercise', label: 'Exercise' },
                { value: 'drill', label: 'Drill' },
                { value: 'tabletop', label: 'Tabletop Exercise' }
              ]}
            />
            <FormSelect
              label="Hazard Type"
              required
              value={formData.hazard_type}
              onChange={(e) => setFormData({ ...formData, hazard_type: e.target.value })}
              options={[
                { value: '', label: 'Select hazard type...' }
              ]}
              groups={[
                { label: 'Natural Hazards', options: [
                  { value: 'earthquake', label: 'Earthquake' },
                  { value: 'flood', label: 'Flood' },
                  { value: 'hurricane', label: 'Hurricane / Typhoon' },
                  { value: 'tornado', label: 'Tornado' },
                  { value: 'wildfire', label: 'Wildfire' },
                  { value: 'winter_storm', label: 'Winter Storm' },
                  { value: 'severe_weather', label: 'Severe Weather / Thunderstorm' },
                  { value: 'drought', label: 'Drought' },
                  { value: 'tsunami', label: 'Tsunami' },
                  { value: 'volcanic_eruption', label: 'Volcanic Eruption' },
                  { value: 'landslide', label: 'Landslide / Mudslide' },
                  { value: 'pandemic', label: 'Pandemic / Epidemic' },
                  { value: 'space_weather', label: 'Space Weather' },
                  { value: 'natural_other', label: 'Other Natural Hazard' },
                ]},
                { label: 'Technological Hazards', options: [
                  { value: 'hazmat_release', label: 'Hazardous Materials Release' },
                  { value: 'industrial_accident', label: 'Industrial Accident' },
                  { value: 'dam_failure', label: 'Dam / Levee Failure' },
                  { value: 'infrastructure_failure', label: 'Infrastructure Failure' },
                  { value: 'pipeline_explosion', label: 'Pipeline Explosion' },
                  { value: 'radiological_release', label: 'Radiological Release' },
                  { value: 'transportation_accident', label: 'Transportation Accident' },
                  { value: 'train_derailment', label: 'Train Derailment' },
                  { value: 'mine_accident', label: 'Mine Accident' },
                  { value: 'technological_other', label: 'Other Technological Hazard' },
                ]},
                { label: 'Human-Caused Incidents', options: [
                  { value: 'active_shooter', label: 'Active Shooter / Assailant' },
                  { value: 'terrorism_ied', label: 'Terrorism / IED' },
                  { value: 'cyber_attack', label: 'Cyber Attack' },
                  { value: 'cbrne', label: 'CBRNE Attack' },
                  { value: 'arson', label: 'Arson / Incendiary' },
                  { value: 'civil_unrest', label: 'Civil Unrest' },
                  { value: 'sabotage', label: 'Sabotage' },
                  { value: 'mass_gathering', label: 'Mass Gathering Incident' },
                  { value: 'human_caused_other', label: 'Other Human-Caused Incident' },
                ]}
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Documents</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                    <span>Upload files</span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setFiles(Array.from(e.target.files))}
                      className="sr-only"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.mp3,.wav,.mp4,.m4a"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF, Word, Excel, Images, Audio files</p>
              </div>
            </div>
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm bg-gray-50 p-2 rounded">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="flex-1">{file.name}</span>
                    <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auto-generate checkbox */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <div className="flex items-start space-x-3">
              <input 
                type="checkbox"
                id="autoGenerate"
                checked={autoGenerate}
                onChange={(e) => setAutoGenerate(e.target.checked)}
                className="mt-1 rounded text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="autoGenerate" className="text-sm font-medium text-gray-900 cursor-pointer">
                  Generate AAR immediately after upload
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  {autoGenerate 
                    ? "✓ AAR will be generated automatically. Processing may take a few minutes."
                    : "Event will be created in 'In Progress' status. You can add more documents, create feedback forms, or generate the AAR later."
                  }
                </p>
              </div>
            </div>
          </div>
            <div className="flex items-center justify-end pt-4">
            <button
              type="submit"
              disabled={uploading}
              className={`px-6 py-3 rounded-lg text-white font-medium transition ${
                uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {uploading ? 'Processing...' : 'Create Event'}
            </button>
          </div>
        </form>
      </Widget>
    </div>
  );
}

export default UploadView;